/**
 * Turning a printer's name into its id, and what an id may be.
 *
 * The rule below is the same one `api/src/lib/ids.ts` enforces, and
 * `web/test/logic.ts` fails if the two ever stop being character-for-character
 * identical. It is duplicated rather than shared because the two packages are
 * built separately; what must not be duplicated is the *decision*, so the test
 * compares the source text rather than trusting anyone to remember.
 *
 * The id matters more than it looks. It is a path segment in
 * `/printers/{printerId}` and a directory in S3 -- `calibration/{printerId}/
 * photo.jpg` -- and the survey Lambda finds that object by splitting the key
 * on `/` and requiring exactly three parts.
 */
export const PRINTER_ID = /^[a-z0-9][a-z0-9-]{1,39}$/;

/**
 * A name a person typed, as an id a machine can key on.
 *
 * Derived rather than asked for, because nobody setting up a printer should
 * have to learn what a DynamoDB key is. The derived id is shown before the
 * printer is created, so "MO-180 front" visibly becomes `mo-180-front` and
 * there is no surprise later when it turns up in a URL.
 */
export function printerIdFrom(name: string): string {
  return name
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')       // "Ångström" -> "Angstrom"
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+/, '')
    .slice(0, 40)
    .replace(/-+$/, '');
}
